import { getCustomTag, sendNotification, useApiClient, useTranslation } from '@msdk/common'
import { CustomActionButtonAspect, DefaultActionType, Entity, ServerOperationResult } from '@msdk/definitions'
import React from 'react'

import { useComponentModal } from '../../../hooks'
import { messagesFromEntityOperation } from '../../../utils'
import { ActionConfirmationModal } from '../../ActionConfirmationModal'
import { ActionVisualizationMode } from '../../FormActions/action-visualization'
import { ActionElement } from '../../FormActions/ActionElement'
import { useDynamicViewContext } from '../context'
import { ActionLabels, getDeleteActionLabels } from './labels-utils'

interface Props {
  row: Entity
  visualizationMode: ActionVisualizationMode
  onClose?: () => void
}

export function DeleteRowAction({ row, visualizationMode, onClose }: Props) {
  const { baseMetadata, reload, viewModel, entityModel } = useDynamicViewContext()

  const { createModal } = useComponentModal()

  const api = useApiClient()
  const t = useTranslation()

  const actionDetails = entityModel.defaultActionsDetails.find((e) => e.type === DefaultActionType.Delete)

  const labels: ActionLabels = getDeleteActionLabels(actionDetails, t)

  const handleClick = async () => {
    onClose && onClose()

    const onConfirm = async () => {
      try {
        const res = await api.entity.deleteEntity(baseMetadata.msProvider, viewModel?.entity, row.id)

        const serverMessages = messagesFromEntityOperation(res)
        if (serverMessages.length) {
          const translated = serverMessages.map((msg) => `${t(msg.idml, msg.description)}`).join('\n')
          sendNotification({ message: translated, status: 'error' })
        } else {
          if (res.result === ServerOperationResult.Success) {
            sendNotification({ message: labels.success, status: 'success' })
            reload()
          } else {
            sendNotification({ message: labels.error, status: 'error' })
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          const regex = /\{[^]*\}/
          const match = error.message.match(regex)

          if (match) {
            const jsonString = match[0]
            const errorObject = JSON.parse(jsonString)
            if (errorObject.status == '409') {
              sendNotification({ message: errorObject.response, status: 'error' })
              return
            }
          }
        }

        sendNotification({ message: labels.error, status: 'error' })
      }
    }

    const modalRef = createModal(
      <ActionConfirmationModal
        title={labels.title}
        body={labels.body}
        onConfirm={onConfirm}
        confirmationLabel={labels.confirmationLabel}
        type={DefaultActionType.Delete}
      />,
      () => {
        //do nothing
      },
    )

    modalRef.open()
  }

  return (
    <ActionElement
      icon={labels.iconTag}
      label={labels.description}
      onClick={handleClick}
      visualizationMode={visualizationMode}
      disabled={false}
      aspect={CustomActionButtonAspect.Alternative}
      testId={getCustomTag(['modal', 'DELETE', 'Delete_Permanently', 'button'])}
    />
  )
}
